// src/notifications/NotificationProvider.jsx
/**
 * NotificationProvider / useNotification — slide-in toasts, bottom left.
 *
 * @description
 *   `useNotification()` returns `{ success, error, warning, info }`. Non-error
 *   toasts auto-dismiss after 5 s; errors stay until closed.
 *
 * @useWhen
 *   - Short feedback on an action ("Saved", "Transition failed")
 *   - A MONEY consequence of the user's own click — pass `{ persist: true }`
 *
 * @dontUseFor
 *   - A condition the user must fix next to a field — use an inline `<Alert>`
 *   - Plain confirmations with `persist` — a sticky "Saved" is noise
 *
 * @props
 *   each method: (message, opts?) — `opts.persist` keeps a non-error toast
 *   until closed (Owner-Entscheid E8, 2026-09-18, B2/W1a)
 */
import React, { createContext, useContext, useState, useMemo, useCallback } from 'react'
import { Alert as MuiAlert, Slide, Stack, Box } from '@mui/material'

const NotificationContext = createContext(null)

let _nextId = 0

function NotificationItem({ item, onDismiss }) {
  const [show, setShow] = useState(true)

  const handleClose = () => {
    setShow(false)
    // Wait for slide-out animation before removing from DOM
    setTimeout(() => onDismiss(item.id), 250)
  }

  return (
    <Slide direction="right" in={show} mountOnEnter unmountOnExit timeout={250}>
      <MuiAlert
        elevation={6}
        variant="filled"
        severity={item.severity}
        onClose={handleClose}
        sx={{ minWidth: 280, maxWidth: 420 }}
      >
        {item.message}
      </MuiAlert>
    </Slide>
  )
}

export const NotificationProvider = ({ children }) => {
  const [items, setItems] = useState([])

  const dismiss = useCallback((id) => {
    setItems(prev => prev.filter(n => n.id !== id))
  }, [])

  // 🔴 `persist` — Owner-Entscheid E8 (2026-09-18), Regel-16-Gate W1a.
  // Eine Meldung, die GELD benennt („5,90 € bleiben abgezogen"), verschwand nach
  // 5 s von selbst; wer in dem Moment woanders hinsah, erfuhr die Folge des
  // eigenen Klicks nie. Fehler bleiben ohnehin stehen — diese Meldungen jetzt
  // auch, und zwar auf Ansage der Aufrufstelle, nicht pauschal.
  const show = useCallback((message, severity = 'info', opts = {}) => {
    const id = ++_nextId
    setItems(prev => [...prev, { id, message, severity }])
    // Auto-dismiss non-error after 5s — ausser die Aufrufstelle verlangt Bestand.
    if (severity !== 'error' && !opts.persist) {
      setTimeout(() => dismiss(id), 5000)
    }
  }, [dismiss])

  const api = useMemo(() => ({
    success: (msg, opts) => show(msg, 'success', opts),
    error:   (msg, opts) => show(msg, 'error', opts),
    warning: (msg, opts) => show(msg, 'warning', opts),
    info:    (msg, opts) => show(msg, 'info', opts),
  }), [show])

  return (
    <NotificationContext.Provider value={api}>
      {children}
      <Box
        sx={{
          position: 'fixed',
          bottom: 24,
          left: 24,
          zIndex: 2000,
          pointerEvents: 'none',
        }}
      >
        <Stack spacing={1} sx={{ '& > *': { pointerEvents: 'auto' } }}>
          {items.map((n) => (
            <NotificationItem key={n.id} item={n} onDismiss={dismiss} />
          ))}
        </Stack>
      </Box>
    </NotificationContext.Provider>
  )
}

export const useNotification = () => {
  const ctx = useContext(NotificationContext)
  if (!ctx) throw new Error('useNotification must be inside NotificationProvider')
  return ctx
}
